import { toast } from 'sonner';
import { ListChecks, RotateCcw } from 'lucide-react';
import {
  usePersonCatalogTasks,
  useSetPersonCatalogTask,
  useReopenPersonCatalogTask,
} from '../../hooks/useOnboardingTasks';
import { CollapsibleSection } from '../ui/CollapsibleSection';
import { Button } from '../ui/Button';
import { Pill } from '../ui/Pill';
import { ApiError } from '../../lib/api';
import { ItemStatusPill, canReopen } from './itemStatus';

const KIND_LABEL: Record<string, string> = { upload: 'Upload', todo: 'To-do' };

/**
 * Assign reusable library tasks (upload / to-do) to one person directly,
 * alongside the per-form / per-document panels. Submitted tasks can be
 * reopened back to pending.
 */
export function PersonCatalogTasksPanel({ personId }: { personId: string }) {
  const tasks = usePersonCatalogTasks(personId);
  const set = useSetPersonCatalogTask(personId);
  const reopen = useReopenPersonCatalogTask(personId);

  if (tasks.isLoading || !tasks.data) return null;
  if (tasks.data.length === 0) return null;

  const assignedCount = tasks.data.filter((t) => t.assigned).length;

  return (
    <CollapsibleSection
      icon={<ListChecks className="h-4 w-4 text-fg-muted" />}
      title="Library tasks"
      trailing={`${assignedCount} assigned`}
    >
      <p className="mb-2 text-xs text-fg-muted">
        Assign any reusable task to this person. Editing a task in the library doesn't change people already assigned.
      </p>
      <ul className="divide-y divide-divider/10">
        {tasks.data.map((t) => (
          <li key={t.id} className="flex items-center justify-between gap-2 py-2.5 text-sm">
            <span className="flex min-w-0 items-center gap-2 text-fg">
              <span className="truncate">{t.title}</span>
              <Pill tone="neutral" size="sm">{KIND_LABEL[t.kind] ?? t.kind}</Pill>
              <ItemStatusPill status={t.status} />
            </span>
            <div className="flex shrink-0 items-center gap-3">
              {t.assigned && canReopen(t.status) ? (
                <Button
                  variant="ghost"
                  size="sm"
                  iconLeft={<RotateCcw className="h-3.5 w-3.5" />}
                  loading={reopen.isPending}
                  onClick={async () => {
                    try {
                      await reopen.mutateAsync(t.id);
                      toast.success('Reopened');
                    } catch (err) {
                      toast.error(err instanceof ApiError ? err.message : 'Could not reopen');
                    }
                  }}
                >
                  Reopen
                </Button>
              ) : null}
              <label className="flex items-center gap-2 text-fg-muted">
                <input
                  type="checkbox"
                  checked={t.assigned}
                  disabled={set.isPending || (!t.assigned && !t.published)}
                  onChange={async (e) => {
                    try {
                      await set.mutateAsync({ taskId: t.id, assigned: e.target.checked });
                    } catch (err) {
                      toast.error(err instanceof ApiError ? err.message : 'Could not update');
                    }
                  }}
                />
                {t.assigned ? 'Assigned' : 'Not assigned'}
              </label>
            </div>
          </li>
        ))}
      </ul>
    </CollapsibleSection>
  );
}
